import {getDocumentMaxFiles} from '@sollapay/domain';
import {DocumentContext, DocumentEntityType, DocumentType} from '@sollapay/enums';
import {FormSectionHeading} from '@sollapay/ui/components';
import {useQueryClient} from '@tanstack/react-query';
import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ErrorAlertMessage} from '@/components';
import {
  DocumentUploadSection,
  getDocumentTypeLabel,
  resolveDocumentError,
  useDocumentRequiredForm
} from '@/features/documents';
import {useShowErrors} from '@/features/trust-accounts/activation/hooks';
import {trustAccountKeys} from '@/features/trust-accounts/hooks';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import type {FormStateSnapshot} from '@/hooks';
import type {FC} from 'react';

interface SupportingDocsStepFormProps {
  trustAccountId: string;
  documentsReadOnly?: boolean;
  transactionLegalDocumentsReadOnly?: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

export const SupportingDocsStepForm: FC<SupportingDocsStepFormProps> = ({
  trustAccountId,
  documentsReadOnly,
  transactionLegalDocumentsReadOnly,
  onFormStateChange
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const queryClient = useQueryClient();
  const strategy = useTrustTypeStrategy();
  const showErrors = useShowErrors();
  const [uploadError, setUploadError] = useState<unknown>(null);

  // Returns a fresh array per call, so memoize it — downstream hooks key off its identity.
  const requiredDocumentTypes = useMemo(
    () => strategy.getRequiredComplianceDocumentTypes(),
    [strategy]
  );

  const handleDocumentChange = () => {
    void queryClient.invalidateQueries({queryKey: trustAccountKeys.detail(trustAccountId)});
  };

  const {missingDocumentTypes} = useDocumentRequiredForm({
    entityType: DocumentEntityType.TRUST_ACCOUNT,
    entityId: trustAccountId,
    documentContext: DocumentContext.COMPLIANCE,
    requiredDocumentTypes,
    onFormStateChange
  });

  const byTypePrefix = 'activation.packages.compliance.supportingDocuments.form.byType';

  const documentError = (documentType: DocumentType) =>
    resolveDocumentError(documentType, missingDocumentTypes, showErrors, t);

  return (
    <div className="flex flex-col gap-6" data-slot="supporting-docs-step-form">
      {uploadError != null && <ErrorAlertMessage error={uploadError} />}
      <FormSectionHeading
        title={t('activation.packages.compliance.supportingDocuments.title')}
        description={t('activation.packages.compliance.supportingDocuments.description')}
      />

      {requiredDocumentTypes.map(documentType => (
        <DocumentUploadSection
          key={documentType}
          title={getDocumentTypeLabel(documentType, t)}
          titleTooltip={t(`${byTypePrefix}.${documentType}.tooltip`, {defaultValue: ''})}
          description={t(`${byTypePrefix}.${documentType}.description`, {defaultValue: ''})}
          examples={
            t(`${byTypePrefix}.${documentType}.examples`, {
              defaultValue: [],
              returnObjects: true
            }) as string[]
          }
          required
          error={documentError(documentType)}
          maxFiles={getDocumentMaxFiles(documentType)}
          entityType={DocumentEntityType.TRUST_ACCOUNT}
          entityId={trustAccountId}
          documentContext={DocumentContext.COMPLIANCE}
          documentType={documentType}
          readOnly={documentsReadOnly}
          onError={setUploadError}
          onDocumentChange={handleDocumentChange}
        />
      ))}

      <DocumentUploadSection
        title={getDocumentTypeLabel(DocumentType.TRANSACTION_LEGAL_DOCUMENT, t)}
        description={t(`${byTypePrefix}.transaction_legal_document.description`)}
        examples={
          t(`${byTypePrefix}.transaction_legal_document.examples`, {
            returnObjects: true
          }) as string[]
        }
        maxFiles={getDocumentMaxFiles(DocumentType.TRANSACTION_LEGAL_DOCUMENT)}
        entityType={DocumentEntityType.TRUST_ACCOUNT}
        entityId={trustAccountId}
        documentContext={DocumentContext.COMPLIANCE}
        documentType={DocumentType.TRANSACTION_LEGAL_DOCUMENT}
        readOnly={transactionLegalDocumentsReadOnly}
        onError={setUploadError}
        onDocumentChange={handleDocumentChange}
      />
    </div>
  );
};
